import { getGatewayBaseUrl } from "../config";
import { PosLocalProjectionGatewayClient, type MemberRuntimeItem } from "../gateway/local-projection-client";
import type { PosSaleMember } from "../gateway/sale-context-client";
import { readLocalEmployeeSession } from "./employee-session";
import { indexedScope, normalizeIdentifier, readLocalProjectionCursor } from "./indexed-runtime";
import { posRuntimeDb } from "./pos-db";
import { readTerminalCredential, type TrustedTerminalIdentity } from "./terminal-state";

const FAST_MEMBER_DELTA_TIMEOUT_MS = 2_500;
const MAX_FAST_MEMBER_DELTA_PAGES = 50;
const fastMemberDeltaInFlight = new Map<string, Promise<void>>();

function clean(value: string | null | undefined) {
  return (value ?? "").trim().toLocaleLowerCase("th").replace(/\s+/g, " ");
}

function tokens(...values: Array<string | null | undefined>) {
  const result = new Set<string>();
  for (const raw of values) {
    const value = clean(raw);
    if (!value) continue;
    result.add(value);
    for (const part of value.split(/[^\p{L}\p{N}@._+-]+/u).filter(Boolean)) result.add(part);
    const compact = normalizeIdentifier(value);
    if (compact) result.add(compact);
  }
  return [...result];
}

function phoneLast4(value: string | null | undefined) {
  const digits = normalizeIdentifier(value).replace(/\D/g, "");
  return digits.length >= 4 ? digits.slice(-4) : null;
}

function memberKey(scopeKey: string, customerId: string) {
  return `${scopeKey}:customer:${customerId}`;
}

function memberIdentifierKey(scopeKey: string, customerId: string, type: string, normalized: string) {
  return `${scopeKey}:customer:${customerId}:identifier:${type}:${normalized}`;
}

function memberFromItem(item: MemberRuntimeItem): PosSaleMember | null {
  if (!item.active) return null;
  return {
    customer_id: item.customer_id,
    membership_id: item.membership_id,
    customer_code: item.customer_code,
    customer_name: item.customer_name,
    phone: item.phone,
    email: item.email,
    membership_status: item.membership_status,
    joined_at: item.joined_at,
    identifiers: (item.identifiers ?? []).map((entry) => ({
      identifier_type: entry.identifier_type,
      display_value: entry.display_value,
      is_primary: entry.is_primary,
    })),
  };
}

async function ensurePhoneLast4Index(scopeKey: string) {
  const missing = await posRuntimeDb.memberIdentifiers
    .where("identifierType")
    .equals("PHONE")
    .and((row) => row.scopeKey === scopeKey && !row.phoneLast4)
    .toArray();
  if (!missing.length) return;
  await posRuntimeDb.memberIdentifiers.bulkPut(
    missing.map((row) => ({ ...row, phoneLast4: phoneLast4(row.normalizedValue) })),
  );
}

export async function readIndexedMemberByCustomerId(
  identity: TrustedTerminalIdentity,
  customerId: string,
): Promise<PosSaleMember | null> {
  const scopeKey = indexedScope(identity, "member");
  const row = await posRuntimeDb.members.get(memberKey(scopeKey, customerId));
  if (!row?.payload || typeof row.payload !== "object") return null;
  return memberFromItem(row.payload as MemberRuntimeItem);
}

async function searchIndexedMembersLocal(
  identity: TrustedTerminalIdentity,
  searchText: string,
  limit: number,
): Promise<PosSaleMember[]> {
  const scopeKey = indexedScope(identity, "member");
  const query = clean(searchText);
  const normalized = normalizeIdentifier(searchText);
  const digits = normalized.replace(/\D/g, "");
  const customerIds: string[] = [];

  if (normalized) {
    const exact = await posRuntimeDb.memberIdentifiers
      .where("[scopeKey+normalizedValue]")
      .equals([scopeKey, normalized])
      .toArray();
    customerIds.push(...exact.map((row) => row.customerId));
  }

  if (/^\d{4}$/.test(digits)) {
    await ensurePhoneLast4Index(scopeKey);
    const suffix = await posRuntimeDb.memberIdentifiers
      .where("[scopeKey+phoneLast4]")
      .equals([scopeKey, digits])
      .toArray();
    customerIds.push(...suffix.map((row) => row.customerId));
  }

  if (customerIds.length < limit && query && !/^\d{4}$/.test(digits)) {
    const prefix = await posRuntimeDb.members
      .where("searchTokens")
      .startsWithIgnoreCase(query)
      .and((row) => row.scopeKey === scopeKey)
      .limit(limit)
      .toArray();
    customerIds.push(...prefix.map((row) => row.customerId));
  }

  if (!query) {
    const rows = await posRuntimeDb.members.where("scopeKey").equals(scopeKey).limit(limit).toArray();
    customerIds.push(...rows.map((row) => row.customerId));
  }

  const unique = [...new Set(customerIds)].slice(0, limit);
  const rows = await Promise.all(unique.map((customerId) => posRuntimeDb.members.get(memberKey(scopeKey, customerId))));
  return rows.flatMap((row) => {
    if (!row?.payload || typeof row.payload !== "object") return [];
    const member = memberFromItem(row.payload as MemberRuntimeItem);
    return member ? [member] : [];
  });
}

function isFastMemberIdentifierLookup(searchText: string) {
  const normalized = normalizeIdentifier(searchText);
  const digits = normalized.replace(/\D/g, "");
  return /^\d{4}$/.test(digits) || /^\+?\d{8,15}$/.test(normalized);
}

async function refreshMemberDeltaOnLocalMiss(identity: TrustedTerminalIdentity) {
  if (typeof navigator === "undefined" || !navigator.onLine) return;

  const scopeKey = indexedScope(identity, "member");
  const existing = fastMemberDeltaInFlight.get(scopeKey);
  if (existing) {
    await existing;
    return;
  }

  const run = (async () => {
    const [terminal, session] = await Promise.all([
      readTerminalCredential(),
      readLocalEmployeeSession(),
    ]);
    const terminalIdentity = terminal?.identity ?? null;
    const actor = session?.actor ?? null;
    const sameScope = Boolean(
      terminalIdentity
      && actor
      && terminalIdentity.organization_id === identity.organization_id
      && terminalIdentity.branch_id === identity.branch_id
      && terminalIdentity.node_id === identity.node_id
      && actor.organization_id === identity.organization_id
      && actor.branch_id === identity.branch_id
      && actor.node_id === identity.node_id
      && actor.runtime_session_status === "ACTIVE",
    );
    if (!sameScope || !terminal?.deviceSecret || !session?.sessionToken) return;

    const baseUrl = getGatewayBaseUrl();
    if (!baseUrl) return;

    const client = new PosLocalProjectionGatewayClient({
      baseUrl,
      timeoutMs: FAST_MEMBER_DELTA_TIMEOUT_MS,
    });
    let cursor = await readLocalProjectionCursor(identity, "MEMBER");

    for (let page = 0; page < MAX_FAST_MEMBER_DELTA_PAGES; page += 1) {
      const result = await client.load({
        deviceSecret: terminal.deviceSecret,
        sessionToken: session.sessionToken,
        domain: "MEMBER",
        cursor,
        limit: 500,
      });
      if (!result.ok || !result.data) return;

      // Fast lookup refresh intentionally does not advance the durable MEMBER cursor.
      // Background sync remains the canonical cursor owner; these idempotent upserts only
      // make newly-created members visible in the local index immediately after a miss.
      for (const item of result.data.items) {
        await upsertLocalMemberRuntime(identity, item);
      }

      cursor = result.data.next_cursor;
      if (!result.data.has_more) return;
    }
  })().finally(() => {
    fastMemberDeltaInFlight.delete(scopeKey);
  });

  fastMemberDeltaInFlight.set(scopeKey, run);
  await run;
}

export async function searchIndexedMembers(
  identity: TrustedTerminalIdentity,
  searchText: string,
  limit = 20,
): Promise<PosSaleMember[]> {
  const local = await searchIndexedMembersLocal(identity, searchText, limit);
  if (local.length || !isFastMemberIdentifierLookup(searchText)) return local;

  await refreshMemberDeltaOnLocalMiss(identity);
  return searchIndexedMembersLocal(identity, searchText, limit);
}

export async function upsertLocalMemberRuntime(identity: TrustedTerminalIdentity, item: MemberRuntimeItem) {
  const scopeKey = indexedScope(identity, "member");
  const key = memberKey(scopeKey, item.customer_id);
  await posRuntimeDb.transaction("rw", posRuntimeDb.members, posRuntimeDb.memberIdentifiers, async () => {
    await posRuntimeDb.memberIdentifiers
      .where("customerId")
      .equals(item.customer_id)
      .and((row) => row.scopeKey === scopeKey)
      .delete();

    if (!item.active) {
      await posRuntimeDb.members.delete(key);
      return;
    }

    const identifierTokens = (item.identifiers ?? []).flatMap((entry) => [entry.normalized_value, entry.display_value]);
    await posRuntimeDb.members.put({
      key,
      scopeKey,
      customerId: item.customer_id,
      membershipId: item.membership_id,
      customerCode: item.customer_code,
      customerName: item.customer_name,
      normalizedName: clean(item.customer_name),
      phone: item.phone,
      email: item.email,
      membershipStatus: item.membership_status,
      joinedAt: item.joined_at,
      searchTokens: tokens(item.customer_name, item.customer_code, item.phone, item.email, ...identifierTokens),
      changedAt: item.changed_at,
      payload: item,
    });

    for (const identifier of item.identifiers ?? []) {
      const normalized = normalizeIdentifier(identifier.normalized_value || identifier.display_value);
      if (!normalized) continue;
      await posRuntimeDb.memberIdentifiers.put({
        key: memberIdentifierKey(scopeKey, item.customer_id, identifier.identifier_type, normalized),
        scopeKey,
        customerId: item.customer_id,
        membershipId: item.membership_id,
        identifierType: identifier.identifier_type,
        normalizedValue: normalized,
        phoneLast4: identifier.identifier_type === "PHONE" ? phoneLast4(normalized) : null,
        displayValue: identifier.display_value,
        isPrimary: identifier.is_primary,
        changedAt: item.changed_at,
      });
    }

    const normalizedPhone = normalizeIdentifier(item.phone);
    if (
      normalizedPhone
      && !(item.identifiers ?? []).some((entry) => normalizeIdentifier(entry.normalized_value || entry.display_value) === normalizedPhone)
    ) {
      await posRuntimeDb.memberIdentifiers.put({
        key: memberIdentifierKey(scopeKey, item.customer_id, "PHONE", normalizedPhone),
        scopeKey,
        customerId: item.customer_id,
        membershipId: item.membership_id,
        identifierType: "PHONE",
        normalizedValue: normalizedPhone,
        phoneLast4: phoneLast4(normalizedPhone),
        displayValue: item.phone,
        isPrimary: true,
        changedAt: item.changed_at,
      });
    }
  });

  return memberFromItem(item);
}
